/**
 * Phase 3.5 — Head-to-head fetcher (online matches only).
 *
 * Uses the room token to authenticate — opponent guestId is never
 * exposed to the client. If the session isn't an online room (bot
 * mode), the helper safely returns null.
 *
 * P0 hotfix — gated by `enabled`. The waiting room previously fetched
 * H2H the moment the player landed, before the opponent had joined.
 * The backend then returned the graceful zero-payload (no opponent
 * guestId on file yet) which the UI rendered as the misleading
 * "First meeting". The hook now waits until the caller flips
 * `enabled` to true (typically when `opponentConnected === true`),
 * and re-fetches on that edge so the real rivalry record lands.
 *
 * P0 hotfix (Phase 3.5.4) — exposes `refetch()` so the Match Summary
 * can re-pull the rivalry record after match_history has been written
 * server-side. Without this, the summary would render the *pre-match*
 * H2H and never reflect the just-finished match.
 */
import { useCallback, useEffect, useState } from 'react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export function useHeadToHead(session, { enabled = true } = {}) {
  // session shape: { roomCode, playerId, token }
  const [h2h, setH2h] = useState(null);
  const [loading, setLoading] = useState(false);

  const doFetch = useCallback(async () => {
    if (!session || !session.roomCode || !session.playerId || !session.token) {
      return null;
    }
    const params = new URLSearchParams({
      roomCode: session.roomCode,
      playerId: session.playerId,
      token: session.token,
    });
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND}/api/head-to-head?${params.toString()}`);
      if (!r.ok) {
        setLoading(false);
        return null;
      }
      const data = await r.json();
      setH2h(data || null);
      setLoading(false);
      return data || null;
    } catch (_e) {
      setLoading(false);
      return null;
    }
  }, [session?.roomCode, session?.playerId, session?.token]);

  useEffect(() => {
    if (!enabled) {
      // Clear any prior payload so we don't render stale "First meeting"
      // data captured before the opponent connected.
      setH2h(null);
      return;
    }
    let cancelled = false;
    doFetch().then(() => {
      if (cancelled) return;
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, doFetch]);

  return { h2h, loading, refetch: doFetch };
}
